import { $ctx } from '@milkdown/utils'

import type { Snippet } from '@/features/snippets/types'

type SnippetReferenceSnapshot = {
  status: 'loading' | 'ready' | 'error'
  snippets: readonly Snippet[]
  hiddenReferenceIds?: readonly string[]
}

export type SnippetReferenceSource = {
  getSnapshot: () => SnippetReferenceSnapshot
  subscribe: (listener: () => void) => () => void
  update: (snapshot: SnippetReferenceSnapshot) => void
  /** Re-runs the failed fetch. Set by the wiring — the NodeView has no query tree. */
  retry?: () => void
}

export function createSnippetReferenceSource(): SnippetReferenceSource {
  let snapshot: SnippetReferenceSnapshot = {
    status: 'loading',
    snippets: [],
  }
  const listeners = new Set<() => void>()

  return {
    getSnapshot: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    update: (next) => {
      snapshot = next
      listeners.forEach((listener) => listener())
    },
  }
}

export type SnippetBlockViewConfig = {
  snippetSource: SnippetReferenceSource
  interactive: boolean
  /**
   * The duty whose script this editor is editing. Absent in every other
   * surface, and its absence is what turns the variant controls off — a
   * Document or a Meeting Note has no duty to customise a snippet for.
   */
  dutyId?: string
  /**
   * Opens the library sheet for this snippet. Absent outside edit mode.
   * With a `dutyId` the sheet opens on that duty's own wording instead of
   * the shared body.
   */
  onEdit?: (snippetId: string, dutyId?: string) => void
}

const defaultSnippetSource = createSnippetReferenceSource()

export const snippetBlockViewConfig = $ctx<
  SnippetBlockViewConfig,
  'snippetBlockViewConfig'
>(
  { snippetSource: defaultSnippetSource, interactive: false },
  'snippetBlockViewConfig'
)
